import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Chip, CircularProgress, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { makeStyles } from "@mui/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomMUITable from "src/components/GeneralComponents/CustomMUITable/CustomMUITable";
import { dd_date_format, show_proper_words } from "src/utils/constant";
import moment from "moment";
import CustomDrawer from "src/components/DrawerForm/CustomDrawer";
import SubscriptionListFilter from "./SubscriptionListFilter";
import roundFilterList from "@iconify/icons-ic/round-filter-list";
import { Icon } from "@iconify/react";
import Label from "src/components/Label";
import { project_type } from "src/config/config";
import { subscription_list_with_page_and_plan } from "src/DAL/WebsitePages/websitepages";
import { convertToTitleCase, htmlDecode } from "src/utils/convertHtml";
import FilteredChip from "src/components/FilteredChip";
import { countries } from "src/utils/country";

const TABLE_HEAD = [
  { id: "number", label: "#", alignRight: false, type: "number" },
  { id: "member_name", label: "Member", alignRight: false },
  { id: "country", label: "Country", alignRight: false },
  { id: "user_type", label: "Type", alignRight: false },
  { id: "page_name", label: "Product", alignRight: false },
  { id: "stripe_mode", label: "Subscription Mode", alignRight: false },
  {
    id: "created_at",
    label: "Subscription Date",
    alignRight: false,
  },
  {
    id: "statusDate",
    label: "Status",
    alignRight: false,
    renderData: (row) => {
      let className = "pending-ticket";
      return (
        <div style={{ display: "flex" }}>
          <Label
            variant="ghost"
            color={
              row.statusDate.includes("Cancels")
                ? "warning"
                : row.statusDate.includes("Trial")
                ? "success"
                : row.statusDate === "Cancelled"
                ? "primary"
                : row.statusDate === "Expired"
                ? "error"
                : "success"
            }
            style={{
              color: row.statusDate === "Cancelled" ? "#daa73c" : "",
              backgroundColor:
                row.statusDate === "Cancelled" ? "#daa73c29" : "",
            }}
          >
            {row.statusDate.includes("Cancels") && (
              <AccessTimeIcon
                style={{ marginRight: "5px", width: "18px", height: "18px" }}
              />
            )}
            {row.statusDate}
          </Label>
          {row?.reason && (
            <Tooltip title={row?.reason}>
              <InfoOutlinedIcon
                style={{
                  marginLeft: "1px",
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
        </div>
      );
    },
  },
];

const useStyles = makeStyles(() => ({
  loadingSection: {
    marginLeft: "45%",
    marginTop: "20%",
    marginBottom: "20%",
    padding: 0,
  },
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

export default function SubscriptionList() {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const classes = useStyles();
  const [pageCount, setPageCount] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [filterDrawerState, setFilterDrawerState] = useState(false);

  const EMPTY_FILTER = {
    start_date: moment().format("YYYY-MM-DD"),
    end_date: moment().format("YYYY-MM-DD"),
    sale_page: null,
    payment_plan: null,
    expiry_in: "",
    expired: "all",
    filter:
      project_type == "dev"
        ? "sandBox"
        : project_type != "dev"
        ? "live"
        : "all",
    expiry_name: null,
    search_text: "",
    country: null,
  };

  const [filterState, setFilterState] = useState(EMPTY_FILTER);
  const [appliedFilterState, setAppliedFilterState] = useState(EMPTY_FILTER);
  const [filterStateUpdated, setFilterStateUpdated] = useState(EMPTY_FILTER);

  const prepareChipData = (filters) => {
    let chipData = { ...filters };
    if (chipData.country?.label) {
      chipData.country = chipData.country.label;
    }
    delete chipData.start_date;
    delete chipData.end_date;
    delete chipData.search_text;
    return chipData;
  };

  const handleOpenFilterDrawer = () => {
    setFilterDrawerState(true);
    let findCountry = countries.find((item) => {
      if (String(item.label) == String(filterStateUpdated.country)) {
        return item;
      }
    });

    setFilterState(appliedFilterState);
    setFilterState((values) => ({
      ...appliedFilterState,
      country: findCountry,
    }));
  };
  const handleCloseFilterDrawer = () => {
    setFilterDrawerState(false);
  };

  const handleChangeOther = (name, value) => {
    setFilterState((values) => ({ ...values, [name]: value }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  const fetchSubscriptions = async () => {
    let postData = { ...appliedFilterState };

    if (postData.sale_page) {
      postData.sale_page = postData.sale_page._id;
    } else {
      postData.sale_page = "";
    }
    if (postData.country) {
      postData.country = postData.country.code;
    } else {
      postData.country = "";
    }
    if (postData.payment_plan) {
      postData.payment_plan = postData.payment_plan._id;
    } else {
      postData.payment_plan = "";
    }

    const result = await subscription_list_with_page_and_plan(
      page,
      rowsPerPage,
      postData,
    );

    if (result.code == 200) {
      let subscription_list = [];
      if (result.subscription.length > 0) {
        subscription_list = result.subscription.map((event, index) => {
          let member_name = "a";
          if (event.user_info?.name) {
            member_name =
              event.user_info?.name +
              " (" +
              (event.user_info?.email ? event.user_info?.email : "N/A") +
              ")";
          }

          let transaction_type = "";
          if (event?.sale_page) {
            transaction_type =
              convertToTitleCase(htmlDecode("sale_page (")) +
              (event?.sale_page ? event?.sale_page?.sale_page_title : "") +
              (event?.payment_request
                ? ` | ` + event?.payment_request?.request_title
                : "") +
              (event?.plan ? ` | ` + event?.plan?.plan_title : "") +
              (event?.transaction_request_type
                ? ` | ` + convertToTitleCase(event?.transaction_request_type)
                : "") +
              ")";
          }

          let event_plan = event.plan;
          let plan_title = "N/A";
          if (event_plan?.plan_title) {
            plan_title = event_plan?.plan_title;
          }
          let payment_access = event_plan?.payment_access;
          if (plan_title != "N/A") {
            if (event_plan?.is_plan_free) {
              plan_title = plan_title + " (Free)";
            } else if (payment_access == "recursion") {
              plan_title = plan_title + " (Recurring)";
            } else if (payment_access == "onetime") {
              plan_title = plan_title + " (OneTime)";
            } else {
              plan_title = plan_title;
            }
          }
          let statusTab = "Active";
          if (event?.stripe_subscription_status) {
            statusTab = show_proper_words(event?.stripe_subscription_status);
          } else if (
            event?.cancelation_requested == true ||
            event?.is_cancelled == true
          ) {
            if (
              event?.cancelation_requested == true &&
              event?.is_cancelled == false
            ) {
              let date = moment(event?.subscription_cancelled_date).format(
                "DD MMMM YYYY",
              );
              statusTab = "Cancels at: " + date;
            } else {
              statusTab = "Cancelled";
            }
          } else if (event?.is_on_trial == true) {
            let date = moment(event?.trial_ending_date).format("DD MMMM YYYY");
            statusTab = "Active: Trial Ends at " + date;
          }

          let findCountry = countries.find((item) => {
            if (
              String(item.code) ==
              String(event?.shipping_object?.address?.country)
            ) {
              return item?.label;
            }
          });
          return {
            ...event,
            member_name: member_name,
            page_name: transaction_type,
            user_type:
              event.user_info?.user_type == "member_user" ? "Member" : "Coach",
            created_at: dd_date_format(event.createdAt),
            invoice_date: event.next_invoice_date
              ? dd_date_format(event.next_invoice_date)
              : "N/A",
            plan_title: plan_title,
            statusDate:
              event?.cancelation_requested == true ||
              event?.is_cancelled == true
                ? statusTab
                : event?.is_on_trial === true
                ? statusTab
                : event.subscription_status === false
                ? "Expired"
                : "Active",
            country: findCountry?.label,
          };
        });
      }

      return {
        subscription_list,
        totalCount: result.total_subscription_count,
        totalPages: result.total_pages,
      };
    } else {
      throw new Error(result.message);
    }
  };

  const {
    data: queryData,
    isLoading: isLoadingSection,
    isFetching,
  } = useQuery({
    queryKey: [
      "subscriptions",
      page,
      rowsPerPage,
      JSON.stringify(appliedFilterState),
    ],
    queryFn: fetchSubscriptions,
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const subscriptionsData = queryData?.subscription_list || [];
  const totalCount = queryData?.totalCount || 0;
  const totalPages = queryData?.totalPages || 0;

  const applyFilters = (filterData) => {
    handleCloseFilterDrawer();
    setAppliedFilterState(filterData);
    setFilterStateUpdated(prepareChipData(filterData));
  };

  const searchFunction = () => {
    setPage(0);
    setPageCount(1);
    const newFilter = { ...filterState, search_text: searchText };
    setAppliedFilterState(newFilter);
    setFilterStateUpdated(prepareChipData(newFilter));
    localStorage.setItem("subscription_list_data", JSON.stringify(newFilter));
  };

  const handleDeleteChip = (data) => {
    setPage(0);
    setPageCount(1);

    if (!data.expiry_name) {
      data.expiry_in = "";
      data.start_date = new Date();
      data.end_date = new Date();
    }
    setFilterState(data);
    setAppliedFilterState(data);
    setFilterStateUpdated(prepareChipData(data));
    localStorage.setItem("subscription_list_data", JSON.stringify(data));
  };

  const handleClearFilter = () => {
    handleCloseFilterDrawer();
    let clear_data = { ...EMPTY_FILTER };
    setPage(0);
    setPageCount(1);
    setSearchText("");
    setFilterState(clear_data);
    setAppliedFilterState(clear_data);
    setFilterStateUpdated(prepareChipData(clear_data));
    localStorage.removeItem("subscription_list_data");
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setPageCount(1);
  };

  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilterState((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const handleChangeDate = (name, value) => {
    setFilterState((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    let filter_data = EMPTY_FILTER;
    let find_filter = localStorage.getItem("subscription_list_data");
    if (find_filter) {
      filter_data = JSON.parse(find_filter);
      if (filter_data.search_text) {
        setSearchText(filter_data.search_text);
      }
    }
    setFilterState(filter_data);
    setAppliedFilterState(filter_data);
    setFilterStateUpdated(prepareChipData(filter_data));
  }, []);

  if (isLoadingSection || (isFetching && !subscriptionsData.length)) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-6 col-sm-12 mb-3">
            <h2>Subscription List</h2>
          </div>
          <div className="col-lg-6 col-sm-12 mb-3 text-end">
            <button
              className="small-contained-button"
              onClick={handleOpenFilterDrawer}
            >
              Filters &nbsp;&nbsp; <Icon icon={roundFilterList} />
            </button>
          </div>
        </div>
        <FilteredChip
          data={filterStateUpdated}
          tempState={filterState}
          EMPTY_FILTER={EMPTY_FILTER}
          onDeleteChip={handleDeleteChip}
          onClear={handleClearFilter}
        />
        <div className="row mt-3">
          {isLoadingSection ? (
            <CircularProgress
              className={classes.loadingSection}
              color="primary"
            />
          ) : (
            <div className="col-12">
              <CustomMUITable
                TABLE_HEAD={TABLE_HEAD}
                data={subscriptionsData}
                className="card-with-background"
                custom_pagination={{
                  total_count: totalCount,
                  rows_per_page: rowsPerPage,
                  page: page,
                  handleChangePage: handleChangePage,
                  onRowsPerPageChange: handleChangeRowsPerPage,
                }}
                custom_search={{
                  searchText: searchText,
                  setSearchText: setSearchText,
                  handleSubmit: searchFunction,
                }}
                pageCount={pageCount}
                totalPages={totalPages}
                handleChangePages={handleChangePages}
                pagePagination={true}
              />
            </div>
          )}
        </div>
      </div>
      <CustomDrawer
        isOpenDrawer={filterDrawerState}
        onOpenDrawer={handleOpenFilterDrawer}
        onCloseDrawer={handleCloseFilterDrawer}
        pageTitle="Filters"
        componentToPassDown={
          <SubscriptionListFilter
            filterState={filterState}
            handleChange={handleChange}
            handleChangeOther={handleChangeOther}
            handleChangeDate={handleChangeDate}
            searchSubmitFilter={() => applyFilters(filterState)}
            handleClearFilter={handleClearFilter}
            selectedPage={selectedPage}
            setSelectedPage={setSelectedPage}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
          />
        }
      />
    </>
  );
}
